/**
 * AudioActivationOverlay · v2.7.65+E104
 * ─────────────────────────────────────
 * Silent auto-primer for browser autoplay policy. No UI is ever shown —
 * the component installs a one-shot document interaction listener that
 * primes every dispatch audio file on the very first user gesture
 * (mousedown/keydown/touchstart/click), then sets
 * `localStorage.rtg_audio_activated = "1"` and unmounts itself.
 *
 * Rationale: the previous full-screen "Enable Audio Notifications" modal
 * was jarring after login. Because a user has already gestured through
 * the login flow, any subsequent interaction on the admin dashboard is
 * a valid autoplay-unlock gesture. Piggy-backing on those gestures
 * keeps the dispatch alarm working without a permission dialog.
 */
import { useEffect, useState } from "react";

const KEY = "rtg_audio_activated";

const FILES = [
  // v2.7.65+E227 · Prime the approved /sounds/ default tones so they play cleanly.
  "/sounds/panic-sos-emergency-alert.mp3",
  "/sounds/online-booking-init.mp3",
  "/sounds/driver-online-alert.mp3",
  "/sounds/chat-message-alert.mp3",
  "/sounds/trip-initiated-alert.mp3",
  // v2.7.65+E120 · Google digital-watch alarm for trip-completed + ticket-pipeline.
  "https://actions.google.com/sounds/v1/alarms/digital_watch_alarm_long.ogg",
];

export default function AudioActivationOverlay() {
  const [activated, setActivated] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(KEY) === "1";
  });

  // React to a sibling tab / dev-tool priming the flag.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onStorage = (e) => {
      if (e.key === KEY && e.newValue === "1") setActivated(true);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (activated) return undefined;
    if (typeof window === "undefined") return undefined;

    let primed = false;

    const prime = async () => {
      if (primed) return;
      primed = true;
      for (const src of FILES) {
        try {
          const a = new Audio(src);
          a.volume = 0.0;
          a.muted = true;
          const p = a.play();
          if (p && typeof p.then === "function") {
            await p
              .then(() => { try { a.pause(); } catch { /* noop */ } })
              .catch(() => { /* still blocked — ignore, next gesture wins */ });
          }
        } catch { /* noop */ }
      }
      try { localStorage.setItem(KEY, "1"); } catch { /* private-mode */ }
      setActivated(true);
    };

    // Attempt immediate silent priming — often succeeds because the
    // login form gesture still counts as a user activation.
    prime();

    // Fallback: prime on the first real interaction anywhere on the page.
    const evts = ["mousedown", "touchstart", "keydown", "click", "pointerdown"];
    const once = () => {
      prime();
      evts.forEach((ev) => window.removeEventListener(ev, once, true));
    };
    evts.forEach((ev) => window.addEventListener(ev, once, true));

    return () => evts.forEach((ev) => window.removeEventListener(ev, once, true));
  }, [activated]);

  // Never render UI — this component is now purely a background primer.
  return null;
}
